/**
 * اسکنِ متن (OCR) برای سریال/کدِ چاپ‌شده روی لیبلِ دستگاه‌ها (v2.150.0).
 *
 * موتور: Tesseract.js (LSTM، زبانِ انگلیسی) — **همه‌چیز از سرورِ خودمان** (`public/ocr/`): CDNهای خارجی از ایران
 * قابلِ اتکا نیستند. فقط بارِ اولِ اسکنِ متن ~۷MB دانلود می‌شود و بعد مرورگر نگه می‌دارد (traineddata در IndexedDB).
 * سریال‌ها لاتین/عدد هستند ⇒ فقط حروفِ لاتین، عدد و چند علامت پذیرفته می‌شود تا «O/0» و «I/1» کمتر قاطی شوند.
 */
let workerPromise = null;
let progressCb = null;

const BASE = `${process.env.PUBLIC_URL || ""}/ocr`;
const WHITELIST = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-/._:#() ";

async function getWorker() {
  if (!workerPromise) {
    workerPromise = (async () => {
      const { createWorker } = await import("tesseract.js");
      const w = await createWorker("eng", 1 /* OEM.LSTM_ONLY */, {
        workerPath: `${window.location.origin}${BASE}/worker.min.js`,
        corePath: `${window.location.origin}${BASE}/core`,
        langPath: `${window.location.origin}${BASE}/lang`,
        gzip: true,
        logger: (m) => { if (progressCb && m.status) progressCb(m); },
      });
      await w.setParameters({
        tessedit_char_whitelist: WHITELIST,
        tessedit_pageseg_mode: "6",          // یک بلوکِ متن (لیبل)
        preserve_interword_spaces: "1",
      });
      return w;
    })().catch((e) => { workerPromise = null; throw e; });
  }
  return workerPromise;
}

/** پیش‌پردازش: خاکستری + کشیدنِ کنتراست + بزرگ‌کردنِ متنِ ریز (Tesseract با حروفِ ~۳۰px بهتر می‌خواند) */
export function prepareCanvas(source, sx, sy, sw, sh) {
  const targetH = Math.max(sh, 360);
  const scale = Math.min(3, Math.max(1, targetH / sh), 2400 / sw);
  const c = document.createElement("canvas");
  c.width = Math.round(sw * scale);
  c.height = Math.round(sh * scale);
  const g = c.getContext("2d", { willReadFrequently: true });
  g.imageSmoothingQuality = "high";
  g.drawImage(source, sx, sy, sw, sh, 0, 0, c.width, c.height);
  const img = g.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  let lo = 255; let hi = 0;
  const gray = new Uint8ClampedArray(d.length / 4);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    const v = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
    gray[j] = v; if (v < lo) lo = v; if (v > hi) hi = v;
  }
  const span = Math.max(1, hi - lo);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    const v = ((gray[j] - lo) * 255) / span;
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** چیزهایی که شبیهِ سریال/کد هستند: هر خط + تکه‌هایی که عدد دارند؛ آنچه بعد از S/N، Serial، No. آمده اول. */
export function serialCandidates(text) {
  const out = [];
  const add = (s) => {
    const v = String(s || "").replace(/^[\s:#.\-_/]+|[\s:#.\-_/]+$/g, "").trim();
    if (v.length >= 3 && /[0-9]/.test(v) && !out.includes(v)) out.push(v);
  };
  const lines = String(text || "").split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  // اول آنچه بعد از S/N یا Serial آمده؛ مدل/P/N عمداً اولویت ندارند (سریال نیستند)
  const SER = /(?:S\/?N|SER(?:IAL)?(?:\s*(?:NO|NUMBER))?|NO)\s*[.:#]?\s*([A-Za-z0-9][A-Za-z0-9\-/.]{2,})/gi;
  lines.forEach((l) => { let m; while ((m = SER.exec(l))) add(m[1]); });
  lines.forEach((l) => l.split(" ").forEach(add));
  lines.forEach((l) => { if (l.length <= 40) add(l); });
  return out.slice(0, 12);
}

/** متنِ یک canvas/تصویر را می‌خواند. onProgress(0..1, برچسب) */
export async function recognizeText(canvas, onProgress) {
  progressCb = (m) => {
    const label = m.status === "recognizing text" ? "در حالِ خواندنِ متن"
      : /load|initializ/i.test(m.status) ? "بارگیریِ موتورِ خواندنِ متن (فقط بارِ اول)" : m.status;
    onProgress?.(Number(m.progress) || 0, label);
  };
  try {
    const w = await getWorker();
    const { data } = await w.recognize(canvas);
    return { text: data.text || "", confidence: data.confidence, candidates: serialCandidates(data.text) };
  } finally { progressCb = null; }
}
